import { APPLICATION_STATUSES, CONSENT_TYPES, type OnboardingStage } from './phase2';

export const CUSTOMER_DASHBOARD_MODES = [
  'ONBOARDING',
  'NO_APPLICATION',
  'APPLICATION_IN_PROGRESS',
  'CONTRACT_SETUP',
  'ACTIVE_REPAYMENT',
  'ATTENTION_REQUIRED',
  'COMPLETED',
] as const;

export type CustomerDashboardMode = (typeof CUSTOMER_DASHBOARD_MODES)[number];

export interface CustomerDashboardModeInput {
  readonly hasCustomerProfile: boolean;
  readonly onboardingComplete: boolean;
  readonly planFullyPaid: boolean;
  readonly applicationStatus: string | null;
  readonly mandateStatus: string | null;
  readonly nextScheduleStatus: string | null;
  readonly requiredActionCodes: readonly string[];
}

const DASHBOARD_ATTENTION_ACTION_CODES = [
  'AUTHORIZE_PAYMENT',
  'CLEAR_OVERDUE',
  'ACTIVATE_MANDATE',
  'ACCEPT_CONTRACT',
  'FINISH_APPLICATION',
] as const;

/** Resolves the customer dashboard state from backend lifecycle truth. */
export function resolveCustomerDashboardModeFromTruth(
  input: CustomerDashboardModeInput,
): CustomerDashboardMode {
  if (!input.hasCustomerProfile || !input.onboardingComplete) {
    return 'ONBOARDING';
  }

  if (input.planFullyPaid || input.applicationStatus === 'COMPLETED') {
    return 'COMPLETED';
  }

  const hasAttention =
    input.nextScheduleStatus === 'PROCESSING' ||
    input.nextScheduleStatus === 'FAILED' ||
    input.requiredActionCodes.some((code) =>
      (DASHBOARD_ATTENTION_ACTION_CODES as readonly string[]).includes(code),
    );

  if (hasAttention) {
    return 'ATTENTION_REQUIRED';
  }

  if (input.applicationStatus === null) {
    return 'NO_APPLICATION';
  }

  if (isApplicationPhaseStatus(input.applicationStatus)) {
    return 'APPLICATION_IN_PROGRESS';
  }

  if (isContractSetupStatus(input.applicationStatus, input.mandateStatus)) {
    return 'CONTRACT_SETUP';
  }

  if (input.applicationStatus === 'ACTIVE' || input.applicationStatus === 'MANDATE_ACTIVE') {
    return 'ACTIVE_REPAYMENT';
  }

  return 'NO_APPLICATION';
}

export interface CustomerJourneyTruthInput {
  readonly onboardingComplete: boolean;
  readonly applicationStatus: string | null;
  readonly mandateStatus: string | null;
  readonly paidInstalmentCount: number | null;
  readonly totalInstalmentCount: number | null;
  readonly planFullyPaid: boolean;
}

export interface CustomerJourneyTruthStage {
  readonly key: string;
  readonly label: string;
  readonly state: 'complete' | 'current' | 'upcoming';
}

/** Builds the eight-stage customer journey without client-side lifecycle inference. */
export function buildCustomerJourneyStages(
  input: CustomerJourneyTruthInput,
): CustomerJourneyTruthStage[] {
  const submittedStatuses = [
    'SUBMITTED',
    'UNDER_REVIEW',
    'CHANGES_REQUESTED',
    'APPROVED',
    'AWAITING_CUSTOMER_ACCEPTANCE',
    'ACCEPTED',
    'AWAITING_MANDATE',
    'MANDATE_ACTIVE',
    'ACTIVE',
    'COMPLETED',
    'REJECTED',
  ];
  const approvedStatuses = [
    'APPROVED',
    'AWAITING_CUSTOMER_ACCEPTANCE',
    'ACCEPTED',
    'AWAITING_MANDATE',
    'MANDATE_ACTIVE',
    'ACTIVE',
    'COMPLETED',
  ];
  const acceptedStatuses = [
    'ACCEPTED',
    'AWAITING_MANDATE',
    'MANDATE_ACTIVE',
    'ACTIVE',
    'COMPLETED',
  ];
  const applicationSubmitted =
    input.applicationStatus !== null && submittedStatuses.includes(input.applicationStatus);
  const reviewComplete =
    input.applicationStatus !== null && approvedStatuses.includes(input.applicationStatus);
  const approvalComplete = reviewComplete;
  const acceptanceComplete =
    input.applicationStatus !== null && acceptedStatuses.includes(input.applicationStatus);
  const mandateComplete =
    input.mandateStatus === 'ACTIVE' ||
    input.applicationStatus === 'ACTIVE' ||
    input.applicationStatus === 'COMPLETED';
  const repaymentStarted =
    input.planFullyPaid ||
    (input.paidInstalmentCount ?? 0) > 0 ||
    ((input.totalInstalmentCount ?? 0) > 0 && mandateComplete);

  const stages = [
    { key: 'onboarding', label: 'Onboarding', complete: input.onboardingComplete },
    { key: 'application', label: 'Application', complete: applicationSubmitted },
    { key: 'review', label: 'Review', complete: reviewComplete },
    { key: 'approval', label: 'Approval', complete: approvalComplete },
    { key: 'acceptance', label: 'Acceptance', complete: acceptanceComplete },
    { key: 'mandate', label: 'Mandate', complete: mandateComplete },
    { key: 'repayment', label: 'Repayment', complete: input.planFullyPaid },
    { key: 'completion', label: 'Completion', complete: input.planFullyPaid },
  ] as const;

  let currentIndex = stages.findIndex((stage) => !stage.complete);
  if (currentIndex === -1) {
    currentIndex = stages.length - 1;
  } else if (repaymentStarted && currentIndex < 6) {
    currentIndex = 6;
  }

  return stages.map((stage, index) => ({
    key: stage.key,
    label: stage.label,
    state: stage.complete ? 'complete' : index === currentIndex ? 'current' : 'upcoming',
  }));
}

export const ONBOARDING_WIZARD_STEP_KEYS = [
  'profile',
  'identity',
  'address',
  'agreements',
] as const;

export type OnboardingWizardStepKey = (typeof ONBOARDING_WIZARD_STEP_KEYS)[number];

/** Authoritative platform version recorded on customer consent acceptance. */
export const CURRENT_CONSENT_DOCUMENT_VERSION = '2026-07-01';

export interface CustomerOnboardingStepView {
  readonly key: string;
  readonly label: string;
  readonly state: 'complete' | 'current' | 'upcoming';
  readonly actionPath: string;
}

export interface SubscriptionSelectionInput {
  readonly id: string;
  readonly status: string;
  readonly updatedAt: string | Date;
}

/** Consent types required before onboarding can be marked complete. */
export const REQUIRED_ONBOARDING_CONSENT_TYPES = CONSENT_TYPES;

export interface OnboardingProgressInput {
  readonly hasCustomerProfile: boolean;
  readonly hasNinDocument: boolean;
  readonly addressCount: number;
  readonly recordedConsentTypes: readonly string[];
}

export interface OnboardingProgressResult {
  readonly onboardingComplete: boolean;
  readonly onboardingStage: OnboardingStage | null;
  readonly steps: readonly CustomerOnboardingStepView[];
}

const ONBOARDING_STEP_DEFINITIONS = [
  {
    key: 'profile',
    label: 'Personal details',
    actionPath: '/app/customer/onboarding?step=profile',
  },
  {
    key: 'identity',
    label: 'Identity verification',
    actionPath: '/app/customer/onboarding?step=identity',
  },
  {
    key: 'address',
    label: 'Address',
    actionPath: '/app/customer/onboarding?step=address',
  },
  {
    key: 'agreements',
    label: 'Agreements',
    actionPath: '/app/customer/onboarding?step=agreements',
  },
] as const;

function stepCompletionFlags(input: OnboardingProgressInput): readonly boolean[] {
  const hasAllConsents = REQUIRED_ONBOARDING_CONSENT_TYPES.every((type) =>
    input.recordedConsentTypes.includes(type),
  );

  return [input.hasCustomerProfile, input.hasNinDocument, input.addressCount > 0, hasAllConsents];
}

export function deriveOnboardingStage(input: OnboardingProgressInput): OnboardingStage | null {
  if (!input.hasCustomerProfile) {
    return null;
  }

  const [profileDone, identityDone, addressDone, consentsDone] = stepCompletionFlags(input);

  if (consentsDone && addressDone && identityDone && profileDone) {
    return 'ONBOARDING_COMPLETE';
  }

  if (addressDone && !consentsDone) {
    const hasPartialConsents = input.recordedConsentTypes.length > 0;
    return hasPartialConsents ? 'CONSENTS_PENDING' : 'PROFILE_COMPLETE';
  }

  return 'PROFILE_INCOMPLETE';
}

export function buildOnboardingSteps(input: OnboardingProgressInput): CustomerOnboardingStepView[] {
  const completions = stepCompletionFlags(input);
  let currentIndex = completions.findIndex((complete) => !complete);

  if (currentIndex === -1) {
    currentIndex = completions.length - 1;
  }

  return ONBOARDING_STEP_DEFINITIONS.map((step, index) => ({
    key: step.key,
    label: step.label,
    actionPath: step.actionPath,
    state:
      completions[index] === true ? 'complete' : index === currentIndex ? 'current' : 'upcoming',
  }));
}

export function computeOnboardingProgress(
  input: OnboardingProgressInput,
): OnboardingProgressResult {
  const onboardingStage = deriveOnboardingStage(input);

  return {
    onboardingComplete: onboardingStage === 'ONBOARDING_COMPLETE',
    onboardingStage,
    steps: buildOnboardingSteps(input),
  };
}

export function resolveOnboardingWizardStep(
  input: OnboardingProgressInput,
): OnboardingWizardStepKey | 'complete' {
  if (!input.hasCustomerProfile) {
    return 'profile';
  }

  if (!input.hasNinDocument) {
    return 'identity';
  }

  if (input.addressCount === 0) {
    return 'address';
  }

  const hasAllConsents = REQUIRED_ONBOARDING_CONSENT_TYPES.every((type) =>
    input.recordedConsentTypes.includes(type),
  );

  if (!hasAllConsents) {
    return 'agreements';
  }

  return 'complete';
}

export function resolveAllowedOnboardingStep(
  requested: OnboardingWizardStepKey | null,
  input: OnboardingProgressInput,
): OnboardingWizardStepKey | 'complete' {
  const nextIncomplete = resolveOnboardingWizardStep(input);

  if (nextIncomplete === 'complete') {
    return requested ?? 'complete';
  }

  const order = ONBOARDING_WIZARD_STEP_KEYS;
  const maxAllowedIndex = order.indexOf(nextIncomplete);

  if (requested === null) {
    return nextIncomplete;
  }

  const requestedIndex = order.indexOf(requested);

  if (requestedIndex === -1) {
    return nextIncomplete;
  }

  if (requestedIndex <= maxAllowedIndex) {
    return requested;
  }

  return nextIncomplete;
}

export function onboardingActionForCurrentStep(input: OnboardingProgressInput): {
  readonly title: string;
  readonly actionPath: string;
} {
  const step = resolveOnboardingWizardStep(input);

  if (step === 'complete') {
    return {
      title: 'Account setup complete',
      actionPath: '/app/customer/onboarding',
    };
  }

  switch (step) {
    case 'profile':
      return {
        title: 'Complete your details',
        actionPath: '/app/customer/onboarding?step=profile',
      };
    case 'identity':
      return {
        title: 'Upload your NIN slip',
        actionPath: '/app/customer/onboarding?step=identity',
      };
    case 'address':
      return {
        title: 'Add your address',
        actionPath: '/app/customer/onboarding?step=address',
      };
    case 'agreements':
      return {
        title: 'Review your agreements',
        actionPath: '/app/customer/onboarding?step=agreements',
      };
  }
}

const SUBSCRIPTION_RELEVANCE: Readonly<Record<string, number>> = {
  ACTIVE: 100,
  MANDATE_ACTIVE: 95,
  AWAITING_MANDATE: 85,
  ACCEPTED: 80,
  AWAITING_CUSTOMER_ACCEPTANCE: 75,
  APPROVED: 70,
  UNDER_REVIEW: 55,
  SUBMITTED: 50,
  CHANGES_REQUESTED: 45,
  DRAFT: 40,
  SUSPENDED: 20,
  COMPLETED: 15,
  DEFAULTED: 10,
  CANCELLED: 10,
  REJECTED: 5,
};

export function selectRelevantSubscription<T extends SubscriptionSelectionInput>(
  subscriptions: readonly T[],
): T | undefined {
  if (subscriptions.length === 0) {
    return undefined;
  }

  return [...subscriptions].sort((left, right) => {
    const rankDiff =
      (SUBSCRIPTION_RELEVANCE[right.status] ?? 0) - (SUBSCRIPTION_RELEVANCE[left.status] ?? 0);

    if (rankDiff !== 0) {
      return rankDiff;
    }

    return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
  })[0];
}

export function isApplicationPhaseStatus(status: string | null): boolean {
  if (status === null) {
    return false;
  }

  return (APPLICATION_STATUSES as readonly string[]).includes(status);
}

export function isContractSetupStatus(
  status: string | null,
  mandateStatus: string | null,
): boolean {
  if (
    status === 'AWAITING_CUSTOMER_ACCEPTANCE' ||
    status === 'ACCEPTED' ||
    status === 'AWAITING_MANDATE' ||
    status === 'APPROVED'
  ) {
    return true;
  }

  if (status === 'MANDATE_ACTIVE') {
    return mandateStatus !== 'ACTIVE';
  }

  return (
    mandateStatus === 'PENDING_ACTIVATION_PAYMENT' ||
    mandateStatus === 'PENDING_PROVIDER_AUTHORIZATION'
  );
}

export function hasRepaymentContext(input: {
  readonly hasCustomerProfile: boolean;
  readonly onboardingComplete: boolean;
  readonly applicationStatus: string | null;
}): boolean {
  if (!input.hasCustomerProfile) {
    return false;
  }

  if (input.applicationStatus === 'ACTIVE' || input.applicationStatus === 'MANDATE_ACTIVE') {
    return true;
  }

  if (input.applicationStatus === 'COMPLETED') {
    return true;
  }

  return false;
}
